import React, { useState, useEffect } from 'react';
import { validateField } from '../../utils/validation/signupValidation';

export const Input = ({ 
  label, 
  error,
  validate = false,
  className = '',
  labelClassName = '',
  type = 'text',
  onChange,
  onValidation,
  ...props 
}) => {
  const [validationState, setValidationState] = useState(null);
  const [debounceTimeout, setDebounceTimeout] = useState(null);

  useEffect(() => {
    return () => {
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }
    };
  }, [debounceTimeout]);

  const handleChange = async (e) => {
    onChange?.(e);

    if (!validate) return;

    // Clear existing timeout
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    // Set new timeout for validation
    const newTimeout = setTimeout(async () => {
      try {
        const result = await validateField(props.name || type, e.target.value);
        setValidationState(result);
        onValidation?.(result);
      } catch (error) {
        console.error('Validation error:', error);
      }
    }, 500); // 500ms debounce

    setDebounceTimeout(newTimeout);
  };

  return (
    <div className={className}>
      {label && (
        <label className={`block text-sm font-medium text-gray-300 mb-1 ${labelClassName}`}>
          {label}
        </label>
      )}
      <div className="relative">
        <input
          type={type}
          className={`w-full px-3 py-2 bg-gray-700 border rounded-md text-white placeholder-gray-400
            focus:outline-none focus:ring-2 transition-colors
            ${validationState?.style || 'border-gray-600 focus:ring-blue-500'}
            ${error ? 'border-red-500' : 'border-gray-600'}
            ${className}`}
          onChange={handleChange}
          {...props}
        />
        {validationState && (
          <div className={`mt-1 text-sm ${
            validationState.isValid ? 'text-green-400' : 'text-red-400'
          }`}>
            {validationState.message}
          </div>
        )}
        {validationState?.requirements && (
          <div className="mt-2 space-y-1">
            {validationState.requirements.map((req, index) => (
              <div 
                key={index} 
                className={`flex items-center gap-2 text-sm ${
                  req.met ? 'text-green-400' : 'text-gray-400'
                }`}
              >
                <div className={`w-1.5 h-1.5 rounded-full ${
                  req.met ? 'bg-green-400' : 'bg-gray-400'
                }`} />
                <span>{req.text}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      {error && !validationState?.message && (
        <p className="mt-1 text-sm text-red-400">{error}</p>
      )}
    </div>
  );
};

export const Select = ({ 
  label, 
  options = [], 
  value, 
  onChange, 
  className = '', 
  labelClassName = '',
  disabled = false,
  required = false,
  error
}) => {
  return (
    <div className={className}>
      {label && (
        <label className={`block text-sm font-medium text-gray-300 mb-1 ${labelClassName}`}>
          {label}
        </label>
      )}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        required={required}
        className={`w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white
          focus:outline-none focus:ring-2 focus:ring-blue-500
          disabled:opacity-50 disabled:cursor-not-allowed
          ${error ? 'border-red-500 focus:ring-red-500' : ''}`}
      >
        {options.map((option, index) => {
          // Handle both object options and primitive options
          const value = typeof option === 'object' ? option.value : option;
          const label = typeof option === 'object' ? option.label : option;
          const isDisabled = typeof option === 'object' ? option.disabled : false;
          
          return (
            <option 
              key={index}
              value={value}
              disabled={isDisabled}
              className="bg-gray-700"
            >
              {label}
            </option>
          );
        })}
      </select>
      {error && (
        <p className="mt-1 text-sm text-red-400">{error}</p>
      )}
    </div>
  );
};

export const TextArea = ({ 
  label, 
  className = '', 
  labelClassName = '', 
  rows = 3,
  error,
  ...props 
}) => (
  <div className={className}>
    {label && (
      <label className={`block text-sm font-medium text-gray-300 mb-1 ${labelClassName}`}>
        {label}
      </label>
    )}
    <textarea
      className={`w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white
        focus:outline-none focus:ring-2 focus:ring-blue-500
        ${error ? 'border-red-500 focus:ring-red-500' : ''}`}
      rows={rows}
      {...props}
    />
    {error && (
      <p className="mt-1 text-sm text-red-400">{error}</p>
    )}
  </div>
);

export const TimeSelect = ({ 
  label, 
  value, 
  onChange, 
  className = '', 
  labelClassName = '',
  error,
  ...props 
}) => {
  return (
    <div className={className}>
      {label && (
        <label className={`block text-sm font-medium text-gray-300 mb-1 ${labelClassName}`}>
          {label}
        </label>
      )}
      <input
        type="time"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white
          focus:outline-none focus:ring-2 focus:ring-blue-500
          ${error ? 'border-red-500 focus:ring-red-500' : ''}`}
        {...props}
      />
      {error && (
        <p className="mt-1 text-sm text-red-400">{error}</p>
      )}
    </div>
  );
};

export const Switch = ({ 
  label, 
  checked = false, 
  onChange, 
  disabled = false 
}) => {
  return (
    <label className="flex items-center justify-between cursor-pointer">
      <span className="text-sm font-medium text-gray-300">{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={onChange}
        disabled={disabled}
        className={`
          relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200
          ${disabled 
            ? 'cursor-not-allowed opacity-50' 
            : checked 
              ? 'bg-red-500' 
              : 'bg-gray-700'
          }
        `}
      >
        <span
          className={`
            inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200
            ${checked 
              ? 'translate-x-6' 
              : 'translate-x-1'
            }
          `}
        />
      </button>
    </label>
  );
};